import React from 'react';
import { motion } from 'framer-motion';
interface SignalCardProps {
  title: string;
  description: string;
  index: number;
  isLeft: boolean;
}
function SignalCard({
  title,
  description,
  index,
  isLeft
}: SignalCardProps) {
  return <motion.div className={`flex ${isLeft ? 'justify-start' : 'justify-end'} w-full`} initial={{
    opacity: 0,
    x: isLeft ? -80 : 80
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true,
    amount: 0.3
  }} transition={{
    duration: 0.7,
    delay: index * 0.15,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-7">
      <div className="w-full md:w-[55%] border border-[#8A8A96]/20 bg-[#0A0A0F] p-6 md:p-8 relative group" data-id="element-8">
        {/* Red accent line on the side */}
        <div className={`absolute top-0 ${isLeft ? 'left-0' : 'right-0'} w-[2px] h-full bg-gradient-to-b from-[#FF2D2D] via-[#FF2D2D]/40 to-transparent`} data-id="element-9" />
        <span className="font-['Bebas_Neue'] text-xs tracking-[0.3em] text-[#8A8A96] mb-2 block" data-id="element-10">
          SIGNAL {String(index + 1).padStart(2, '0')}
        </span>
        <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-[#FF2D2D] tracking-wider mb-3" data-id="element-11">
          {title}
        </h3>
        <p className="text-[#8A8A96] text-sm md:text-base leading-relaxed font-light" data-id="element-12">
          {description}
        </p>
      </div>
    </motion.div>;
}
function ConnectorLine({
  index,
  isGoingRight
}: {
  index: number;
  isGoingRight: boolean;
}) {
  const pathD = isGoingRight ? 'M40 0 V20 H260 V40' : 'M260 0 V20 H40 V40';
  return <div className="hidden md:flex justify-center w-full py-2" data-id="element-13">
      <svg width="300" height="40" viewBox="0 0 300 40" fill="none" className="overflow-visible" data-id="element-14">
        <motion.path d={pathD} stroke="#FF2D2D" strokeWidth="1" fill="none" initial={{
        pathLength: 0
      }} whileInView={{
        pathLength: 1
      }} viewport={{
        once: true,
        amount: 0.5
      }} transition={{
        duration: 0.8,
        delay: 0.2 + index * 0.1,
        ease: 'easeInOut'
      }} data-id="element-15" />
        <motion.circle cx={isGoingRight ? '260' : '40'} cy="40" r="3" fill="#FF2D2D" initial={{
        opacity: 0,
        scale: 0
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        delay: 0.8 + index * 0.1
      }} data-id="element-16" />
      </svg>
    </div>;
}
const signals = [{
  title: 'Catalog Entropy',
  description: 'Games lost in noise. 47,000+ titles competing for finite lobby space with zero intelligent curation.'
}, {
  title: 'Recommendation Decay',
  description: "Stale suggestions eroding trust. Static algorithms serving yesterday's preferences to today's players."
}, {
  title: 'Retention Erosion',
  description: 'Players drift away silently. No early-warning system for disengagement patterns across your portfolio.'
}, {
  title: 'Attribution Fog',
  description: 'Unclear ROI on every integration. Content performance data fragmented across incompatible systems.'
}];
export function BrokenSignals() {
  return <section className="relative w-full py-24 md:py-32 px-6 md:px-12 bg-[#050508]" data-id="element-17">
      {/* Section header */}
      <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20" data-id="element-18">
        <motion.h2 className="font-['Bebas_Neue'] text-5xl md:text-7xl tracking-wider text-[#C4C4CC] mb-4" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.7
      }} data-id="element-19">
          BROKEN SIGNALS
        </motion.h2>
        <motion.div className="w-24 h-[1px] bg-[#FF2D2D] mx-auto mb-6" initial={{
        scaleX: 0
      }} whileInView={{
        scaleX: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} data-id="element-20" />
        <motion.p className="text-[#8A8A96] text-base md:text-lg font-light" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.4
      }} data-id="element-21">
          The signals your current stack is missing
        </motion.p>
      </div>

      {/* Zigzag cards */}
      <div className="max-w-5xl mx-auto flex flex-col gap-0" data-id="element-22">
        {signals.map((signal, i) => <div key={signal.title} data-id="element-23">
            <SignalCard title={signal.title} description={signal.description} index={i} isLeft={i % 2 === 0} data-id="element-24" />
            {i < signals.length - 1 && <ConnectorLine index={i} isGoingRight={i % 2 === 0} data-id="element-25" />}
          </div>)}
      </div>
    </section>;
}